import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Optional, ViewChild } from '@angular/core';
import { PRODUCT_VIEW_MODEL_CHANGE_EVENT_NAME } from '@df/catalog-ui/product/view/product-view.component.def';
import { IProductDataV3 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ProductDecorator } from '../../../../model/product/product.decorator';
import { ProductLayoutViewComponent } from '../../../product/layout/view/product-layout-view.component';
import { CuralateGalleryDirective } from './../../../../../module/curalate/curalate-gallery.directive';

export interface IPageComponentCuralateGalleryData extends NICmsContentComponent.IData {
    title?: string;
    copy?: string;
    gallery_id: string;
}

export const PAGE_COMPONENT_CURALATE_GALLERY_ID = 'curalate-gallery';

const config = {
    id: PAGE_COMPONENT_CURALATE_GALLERY_ID,
    name: 'Curalate Gallery',
    tags: ['Homepage'],
    fields: [
        <NICmsContentField.IField>{
            key: 'title',
            title: 'Title',
            type: NICmsContentField.EFieldType.text
        },
        <NICmsContentField.IField>{
            key: 'copy',
            title: 'Copy',
            type: NICmsContentField.EFieldType.textarea
        },
        <NICmsContentField.IField>{
            key: 'gallery_id',
            title: 'Gallery Id',
            type: NICmsContentField.EFieldType.text,
            required: true
        }
    ]
};

@Component({
    selector: 'page-component-curalate-gallery',
    templateUrl: './curalate-gallery.html',
    styleUrl: './curalate-gallery.scss',
    standalone: true,
    imports: [CuralateGalleryDirective, ICmsClientModule, NgClass],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageComponentCuralateGalleryComponent>(config)
export class PageComponentCuralateGalleryComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: IPageComponentCuralateGalleryData;

    filter = '';

    /**
     * Check timeout id
     */
    protected checkTimeoutId;
    /**
     * Max check tries after refreshed
     */
    protected WAITING_MAX = 5;
    /**
     * Check tries count
     */
    protected waiting = 0;

    @ViewChild(CuralateGalleryDirective)
    galleryRef!: CuralateGalleryDirective;

    private readonly app = inject(App);

    constructor(@Optional() protected productLayoutViewComponent: ProductLayoutViewComponent) {
        super();

        // if product view component we subscribe to product change
        if (this.productLayoutViewComponent) {
            this.subscriptions.add(
                this.app.events
                    .on(PRODUCT_VIEW_MODEL_CHANGE_EVENT_NAME)
                    .subscribe((model: ProductDecorator<IProductDataV3>) => this.onProductChange(model))
            );

            if (this.productLayoutViewComponent.model) {
                this.onProductChange(this.productLayoutViewComponent.model);
            }
        }
    }

    /**
     * on loaded callback
     */
    onLoaded(result: boolean): void {
        this.clear();

        if (result) {
            this.check();
        }
    }

    /**
     * Returns if curalate gallery has items
     * @returns {boolean}
     */
    get hasContent(): boolean {
        if (this.galleryRef) {
            return this.galleryRef.hasContent();
        }

        return false;
    }

    /**
     * On product model change callback
     * @param {ProductDecorator<IProductDataV3>} product
     */
    protected onProductChange(product: ProductDecorator<IProductDataV3>): void {
        this.filter = this.getFilter(product);

        this.detectChanges();
    }

    protected getFilter(product: ProductDecorator<IProductDataV3>): string {
        if (product?.data) {
            const sku = product.parent?.isConfigurable ? product.parent.data?.sku : product.data.sku;

            return `productId:${sku}`;
        }

        return '';
    }

    /**
     * Checks if curalate widgets have content
     */
    protected check(): void {
        this.detectChanges();

        if (!this.hasContent && this.waiting < this.WAITING_MAX) {
            this.waiting++;
            this.checkTimeoutId = this.timeoutService.setTimeout(() => this.check(), 200);
        }
    }

    protected clear() {
        this.clearCheckTimeout();
        this.waiting = 0;

        this.detectChanges();
    }

    /**
     * Clears check timeout
     */
    protected clearCheckTimeout(): void {
        if (this.checkTimeoutId) {
            clearTimeout(this.checkTimeoutId);
        }
    }
}
